import RenderManager from "../rendering/render-manager";
import * as Events from "../utils/events";
import EntranceExit from "./EntranceExit";
import Base2DElementDynamic from "./base-2d-element-dynamic";
import Transition from "./Transition";

const min = 20;
class Lottie extends Base2DElementDynamic {

    _initDefaultVars() {
        super._initDefaultVars();
        this._wh = new THREE.Vector2(0.4, 0.4);

        this._src = "";
        this._speed = 1;
        this._loop = true;
        this._autoplay = true;
        this._direction = 1;
    }

    get src() { return this._src; }
    set src(val) { this._src = val; }

    get speed() { return this._speed; }
    set speed(val) { this._speed = val; if (this.anim) this.anim.setSpeed(val) }

    get loop() { return this._loop; }
    set loop(val) {
        this._loop = val;
        if (this.anim) {
            this.anim.loop = val;
            if (this.anim.isPaused && val) this.anim.play()
        }
    }
    get autoplay() { return this._autoplay; }
    set autoplay(val) { this._autoplay = val; if (this.anim && this.anim.isPaused) this.anim.play() }

    get direction() { return this._direction; }
    set direction(val) { this._direction = val; if (this.anim) this.anim.setDirection(val) }

    get type2d() { return "Lottie"; }
    get x1() {return this.x}
    get x2() {return this.x + this.width}
    get y1() {return this.y}
    get y2() {return this.y + this.height}
    get highLightElement() {
        return this.pictureElement
    }
    get points() {
        return [
            {x: this.x1, y: this.y1},
            {x: this.x2, y: this.y1},
            {x: this.x2, y: this.y2},
            {x: this.x1, y: this.y2}
        ]
    }

    _initValues(options) {
        this._src = "src" in options ? options.new ? options.src : Q3.s3Url + options.src : this._src;
        this._speed = "speed" in options ? options.speed : this._speed;
        this._loop = "loop" in options ? options.loop : this._loop;
        this._autoplay = "autoplay" in options ? options.autoplay : this._autoplay;
        this._direction = "direction" in options ? options.direction : this._direction;
    }

    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);

        parentLayer.domElement.setAttribute("contains-lottie", "");

        this.pictureElement = document.createElement('div');

        if (this.disableEvents && !Q3.editMode) {
            this.pictureElement.style.pointerEvents = "none";
        }

        this.pictureElement.className = 'q3-picture thumbs-ignore';
        this.pictureElement.style.width = "100%";
        this.pictureElement.style.height = "100%";

        this.domElement.appendChild(this.pictureElement);

        if (Q3.isReady) {
            this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
            this.enterExitCtrl.init(options);
        } else {
            Q3.Events.listen("tourReady", () => {
                this.enterExitCtrl = new EntranceExit(this, [this.domElement]);
                this.enterExitCtrl.init(options);
            })
        }

        this.pictureElement.addEventListener('click', e => {
            if (Q3.editMode) {
                this.selectHandler(e);
            } else {
                if (this.anim.isPaused) {
                    this.anim.play();
                } else {
                    this.anim.pause();
                }
            }
        }, false);

        let action = [];
        if(this.data) {
            action = this.data.action;
        }
        if (action && action.length && !Q3.editMode) {
            this.pictureElement.classList.add("clickable");
            action.forEach(currAction => {
                const transition = new Transition(currAction.systemAction, currAction.value, this);
                if (currAction.userAction === "click") {
                    this.domElement.addEventListener('click', (e) => {
                        transition.click();
                    }, false);
                }
            });
        }

        const { width, height } = RenderManager.getSize();

        this.redraw = this.redraw.bind(this);

        this.handleListeners(true);

        this.redraw(width, height);

        let animData = {
            container: this.pictureElement,
            renderer: 'svg',
            loop: this.loop,
            autoplay: this.autoplay,
            rendererSettings: {
                progressiveLoad:true,
                preserveAspectRatio:'xMidYMid meet'
            },
            path: this.src
        };
        awaitLib().then(() => {
            this.anim = lottie.loadAnimation(animData);
            this.anim.setSubframe(false);
            this.anim.setSpeed(this.speed);
            this.anim.setDirection(this.direction);

        })
    }

    play() {
        this.anim.play();
    }

    pause() {
        this.anim.pause();
    }

    handleListeners(add) {
        Events[add ? "listen" : "unlisten"]("viewportchanged", this.redraw);
        Events[add ? "listen" : "unlisten"]("splashremoved", this.redraw);
    }

    playLeavingAnimation() {
        this.enterExitCtrl.playLeave();
    }

    cancelLeavingAnimation() {
        this.enterExitCtrl.cancelLeave();
    }

    toggleEvents(enabled) {
        this.disableEvents = !enabled;
        this.pictureElement.style.pointerEvents = enabled || Q3.editMode ? "auto" : "none";
    }

    setData(data) {

        let currentSize = this.getVWSize();
        Object.assign(this[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });

        this.data[currentSize] = {};

        Object.assign(this.data[currentSize], {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height
        });
        if (currentSize === "sizeSm") {
            this.data.sizeSm.autoResized = true;
        }
        this.redraw();

    }

    calcSize(w, h) {
        if (this.ratio === 1) {
            let side = Math.min(w, h);
            return {
                width: side + "px",
                height: side + "px"
            }
        } else if (h > w * this.ratio) {
            return {
                width: w + "px",
                height: w * this.ratio + "px"
            }
        } else {
            return {
                width: h / this.ratio + "px",
                height: h + "px"
            }
        }
    }

    getBBox(w, h) {
        const size = this.getRenderPositionAndSize(w, h);

        return [size.x, size.y, size.x + size.w, size.y + size.h];
    }

    getRenderPositionAndSize(width, height) {
        const pr = RenderManager.pixelRatio;

        if (!width) width = RenderManager.domElement.width / pr;
        if (!height) height = RenderManager.domElement.height / pr;

        let size = this.getVWSize();
        if (this[size]) {
            Object.assign(this, this[size]);
        }

        if (!width) {
            width = size.width;
        }
        if (!height) {
            height = size.height;
        }

        if (!Q3.editMode && this.inLetterbox !== false) {
            width = Q3.letterbox.offsetWidth / pr;
            height = Q3.letterbox.offsetHeight / pr;
        }

        let w = this.width * width * pr;
        let h = this.height * height * pr;

        if (w < min) w = min;
        if (h < min) h = min;

        const [x, y] = this._calcPosition(width, height);

        return { x, y, w, h };
    }

    redraw(width, height) {

        if (!width) width = RenderManager.domElement.width / RenderManager.pixelRatio;
        if (!height) height = RenderManager.domElement.height / RenderManager.pixelRatio;

        const { x, y, w, h } = this.getRenderPositionAndSize(width, height);

        Object.assign(this.domElement.style, {
            width: w + 'px',
            height: h + 'px'
        });


        if (Q3.selected === this && this.target && Q3.editMode) {
            Events.fire('viewportchange');
        }

        this.domElement.style.left = (x || 0) + "px";
        this.domElement.style.top = (y || 0) + "px";
        this.domElement.style.position = "absolute";
        if (w >= 0) this.domElement.style.width = w;
        if (h >= 0) this.domElement.style.height = h;

        // let size = this.calcSize(w, h);
        // Object.assign(this.pictureElement.style, size);
    }

    delete() {

        this.handleListeners(false);

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        this.parentLayer.domElement.removeChild(this.domElement);

        if (this.targetUuid) {
            for (let i = 0; i < Q3.layers.attachedObjects.length; i++) {
                if (Q3.layers.attachedObjects[i].uuid === this.uuid) {
                    Q3.layers.attachedObjects.splice(i, 1);
                    break;
                }
            }
        }

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }


    clone() {

        const picture = new Picture({
            src: this.src,
            x: this.x + 0.01,
            y: this.y + 0.01,
            targetUuid: this.targetUuid
        },
            this.parentLayer,
            THREE.Math.generateUUID()
        );

        Q3.data.layers.layers2D[picture.parentLayer.uuid].objects[picture.uuid] = {
            type: 'picture',
            src: picture.filename,
            x: picture.x,
            y: picture.y,
            targetUuid: picture.targetUuid
        };

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 0, picture);
        this.parentLayer.objects.forEach((o, i) => o.index = i);

        return picture;

    }

    hide() {

        this.pictureElement.classList.add('q3-picture-occluded');

    }

    show() {

        this.pictureElement.classList.remove('q3-picture-occluded');

    }

}

export default Lottie;
export { Lottie };
const awaitLib = (function () {
    let promise;
    return function () {
        return promise = (promise || new Promise(resolve => {
            if (window.lottie) {
                resolve()
            } else {
                console.warn("Begun downloading Lottie");
                injectJS("/lib/lottie.min.js").then(function () {
                    console.info("Lottie downloaded.");
                    resolve();
                });
            }

        }));
    };
})();